import { observable, action } from 'mobx';
import _ from 'lodash';

import { Bloodwork, BloodworkRange } from '../models';
import { BloodworkStore } from './bloodwork-store';
import { LoadingStore } from './loading-store';
import { UserProfileUIStore } from './user-profile-ui-store';

export class BloodworkEntryStore {
  static DEFAULT_LIMIT: number = 5;
  
  @observable bloodworks: Bloodwork[] = [];
  @observable clientBloodworks: Bloodwork[] = [];
  @observable bloodworkRanges: BloodworkRange[] = [];
  @observable currentBloodwork: Bloodwork = new Bloodwork();
  @observable hideEnterTestResults: boolean = false;

  constructor(
    private bloodworkStore: BloodworkStore, 
    private loadingStore: LoadingStore,
    private userProfileUIStore: UserProfileUIStore) {
  }

  @action refreshBloodworks = (): Promise<any> => {
    console.log('***************** refreshBloodworks **********************************');
    this.bloodworks = [];
    return this.loadBloodworks(BloodworkEntryStore.DEFAULT_LIMIT);
  }

  @action loadClientBloodworks = async (clientId: string): Promise<Bloodwork[]> => {
    this.loadingStore.appIsBusy();
    
    const clientBloodworks = await this.bloodworkStore.load(clientId);
    this.clientBloodworks = _.orderBy(clientBloodworks, ['collectionDate', 'createDate'], ['desc', 'desc']);
    
    this.loadingStore.appIsNotBusy();
    
    return this.clientBloodworks;
  }

  @action resetClientBloodworks(): void {
    this.clientBloodworks = [];
  }

  @action setHideEnterTestResults(hideEnterTestResults: boolean): void {
    this.hideEnterTestResults = hideEnterTestResults;
  }

  @action loadBloodworks = (limit: number, lastCreateDate?: string): Promise<any> => {
    console.log('***************** loadBloodworks **********************************');
    this.loadingStore.appIsBusy();
    return this.bloodworkStore.load(undefined, limit, lastCreateDate).then((data) => {
      this.bloodworks.push(...data.bloodworks);
      this.loadingStore.appIsNotBusy();
      return data;
    }, (error: any) => {
      console.error(error);
      this.loadingStore.appIsNotBusy();
      return {};
    });
  }

  @action setCurrentBloodwork = (bloodwork: Bloodwork): void => {
    this.currentBloodwork = bloodwork;
  }

  @action resetCurrentBloodwork = (): Bloodwork => {
    this.currentBloodwork = new Bloodwork();
    return this.currentBloodwork;
  }

  loadBloodworkRanges(): Promise<BloodworkRange[]> {
    this.loadingStore.appIsBusy();
    return this.bloodworkStore.loadBloodworkRanges().then((bloodworkRanges) => {
      this.bloodworkRanges = bloodworkRanges;
      this.loadingStore.appIsNotBusy();
      return bloodworkRanges;
    }, (error: any) => {
      console.error(error);
      this.loadingStore.appIsNotBusy();
      return [];
    });
  }

  saveBloodwork(bloodwork: Bloodwork): Promise<Bloodwork> {
    this.loadingStore.appIsBusy();
    const wasNew = bloodwork.isNew();
    return this.bloodworkStore.saveBloodwork(bloodwork).then((savedBloodwork) => {
      if (wasNew) {
        this.userProfileUIStore.currentUserProfile.incrementNumTests();
        this.userProfileUIStore.saveCurrentUserProfile();
      }
      this.loadingStore.appIsNotBusy();
      return savedBloodwork;
    }, (error: any) => {
      console.error(error);
      this.loadingStore.appIsNotBusy();
      throw error;
    });
  }

  @action deleteBloodwork(bloodwork: Bloodwork): Promise<any> {
    this.loadingStore.appIsBusy();
    return this.bloodworkStore.deleteBloodwork(bloodwork).then((result) => {
      return this.refreshBloodworks();
    }).then(() => {
      this.loadingStore.appIsNotBusy();
    }).catch((error) => {
      console.error(error);
      this.loadingStore.appIsNotBusy();
    });
  }

  @action deleteClientBloodworks(clientId: string): Promise<any> {
    if (this.clientBloodworks.length === 0) {
      return Promise.resolve();
    }

    this.loadingStore.appIsBusy();
    const promises = [];
    this.clientBloodworks.forEach((bloodwork) => {
      promises.push(this.bloodworkStore.deleteBloodwork(bloodwork));
    });
    return Promise.all(promises).then(() => {
      return this.loadClientBloodworks(clientId);
    }, (error) => {
      console.error(error);
      return this.loadClientBloodworks(clientId);
    }).catch((error) => {
      console.error(error);
      return this.loadClientBloodworks(clientId);
    });
  }
}